import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";

/**
 * 載入中的視覺元件 —— 改寫自 loading-ui（https://www.loading-ui.com，MIT License，
 * https://github.com/turbostarter/loading-ui）。
 *
 * 原版是 Tailwind class＋每個實例各塞一段 <style>；這個專案沒有 Tailwind，所以：
 *   · 版面改成 MUI 的 sx；
 *   · keyframes 收成一份 <LoadingKeyframes />，由 theme/MuiThemeBridge 掛一次。
 * 顏色一律 currentColor（跟著外層字色走），尺寸一律 em（跟著字級走，大字模式自動放大）。
 *
 * 用法挑選：
 *   · 按鈕送出中 → MUI Button 的 loading（主題已把 loadingIndicator 換成 <Ring />）
 *   · 整頁／整個區塊在讀 → <PageLoading />（轉圈＋掃光文字）
 *   · 一行字的位置在讀（下拉、徽章旁）→ <InlineLoading />（跳動圓點＋文字）
 *   · 清單、表格、卡片在讀 → <SkeletonRows /> / <SkeletonCard />（先畫出內容的輪廓）
 */

export function LoadingKeyframes() {
  return (
    <GlobalStyles
      styles={{
        "@keyframes loading-ui-ring-spin": { to: { transform: "rotate(360deg)" } },
        "@keyframes loading-ui-dots-blink": { "0%, 100%": { opacity: 0.2 }, "20%": { opacity: 1 } },
        "@keyframes loading-ui-wave-bars": {
          "0%, 100%": { transform: "scaleY(1)", opacity: 0.5 },
          "50%": { transform: "scaleY(0.6)", opacity: 1 },
        },
        "@keyframes loading-ui-skeleton-pulse": { "50%": { opacity: 0.5 } },
        "@keyframes loading-ui-text-shimmer": {
          from: { backgroundPosition: "100% center" },
          to: { backgroundPosition: "0% center" },
        },
        /* 系統設定「減少動態」的人：動畫放慢到幾乎靜止，仍保留「正在讀」的樣子 */
        "@media (prefers-reduced-motion: reduce)": {
          "[data-loading-ui]": { animationDuration: "6s !important" },
          "[data-loading-ui] > *": { animationDuration: "6s !important" },
        },
      }}
    />
  );
}

/** 讀螢幕用的隱藏文字 */
const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

/** 轉圈（loading-ui「ring」）。size 是 em，預設 1em＝跟旁邊的字一樣大 */
export function Ring({ size, label, sx, ...rest }) {
  return (
    <Box
      component="span"
      role={label ? "status" : undefined}
      sx={{ display: "inline-flex", lineHeight: 0, flexShrink: 0, ...sx }}
      {...rest}
    >
      <Box
        component="svg"
        data-loading-ui=""
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        sx={{ width: `${size}em`, height: `${size}em`, animation: "loading-ui-ring-spin 1s linear infinite" }}
      >
        <path
          d="M21 12.0004C20.9999 13.901 20.3981 15.7528 19.2809 17.2904C18.1637 18.8279 16.5885 19.9723 14.7809 20.5596C12.9733 21.1469 11.0262 21.1468 9.21864 20.5594C7.41109 19.9721 5.83588 18.8276 4.71876 17.29C3.60165 15.7523 2.99999 13.9005 3 11.9999C3.00001 10.0993 3.60171 8.24755 4.71884 6.70994C5.83598 5.17233 7.4112 4.02785 9.21877 3.44052C11.0263 2.85319 12.9734 2.85316 14.781 3.44044"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
      {label ? <Box component="span" sx={srOnly}>{label}</Box> : null}
    </Box>
  );
}

Ring.propTypes = { size: PropTypes.number, label: PropTypes.string, sx: PropTypes.object };
Ring.defaultProps = { size: 1, label: "", sx: {} };

/** 跳動圓點（loading-ui「dots」）。寬度是 em，三顆點平均分配 */
export function Dots({ count, size, label, sx, ...rest }) {
  return (
    <Box
      component="span"
      role="status"
      sx={{ display: "inline-flex", alignItems: "center", gap: "12%", width: `${size}em`, ...sx }}
      {...rest}
    >
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          component="span"
          data-loading-ui=""
          aria-hidden="true"
          sx={{
            aspectRatio: "1",
            flexGrow: 1,
            borderRadius: "50%",
            bgcolor: "currentColor",
            animation: "loading-ui-dots-blink 1.4s infinite both",
            animationDelay: `${0.2 * i}s`,
          }}
        />
      ))}
      <Box component="span" sx={srOnly}>
        {label}
      </Box>
    </Box>
  );
}

Dots.propTypes = { count: PropTypes.number, size: PropTypes.number, label: PropTypes.string, sx: PropTypes.object };
Dots.defaultProps = { count: 3, size: 1.6, label: "載入中", sx: {} };

/** 條狀波動（loading-ui「bars」）。高度是 em */
export function Bars({ count, size, label, sx, ...rest }) {
  return (
    <Box
      component="span"
      role="status"
      sx={{ display: "inline-flex", alignItems: "stretch", gap: "5%", width: `${size}em`, height: `${size}em`, ...sx }}
      {...rest}
    >
      {Array.from({ length: count }, (_, i) => (
        <Box
          key={i}
          component="span"
          data-loading-ui=""
          aria-hidden="true"
          sx={{
            display: "inline-block",
            width: `${100 / count}%`,
            borderRadius: "1px",
            bgcolor: "currentColor",
            animation: "loading-ui-wave-bars 1.2s ease-in-out infinite",
            animationDelay: `${0.2 * i}s`,
          }}
        />
      ))}
      <Box component="span" sx={srOnly}>
        {label}
      </Box>
    </Box>
  );
}

Bars.propTypes = { count: PropTypes.number, size: PropTypes.number, label: PropTypes.string, sx: PropTypes.object };
Bars.defaultProps = { count: 3, size: 1, label: "載入中", sx: {} };

/** 文字掃光（loading-ui「text-shimmer」，原版用 motion，這裡改成純 CSS 動畫） */
export function TextShimmer({ children, component, sx, ...rest }) {
  const spread = `${String(children).length * 2}px`;
  return (
    <Box
      component={component}
      data-loading-ui=""
      sx={{
        position: "relative",
        display: "inline-block",
        m: 0,
        color: "text.secondary",
        backgroundImage: `linear-gradient(90deg, #0000 calc(50% - ${spread}), currentColor, #0000 calc(50% + ${spread})), linear-gradient(color-mix(in oklab, currentColor 55%, transparent), color-mix(in oklab, currentColor 55%, transparent))`,
        backgroundSize: "250% 100%, auto",
        backgroundRepeat: "no-repeat, padding-box",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        animation: "loading-ui-text-shimmer 2s linear infinite",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

TextShimmer.propTypes = { children: PropTypes.string, component: PropTypes.elementType, sx: PropTypes.object };
TextShimmer.defaultProps = { children: "載入中…", component: "span", sx: {} };

/** 單塊骨架（loading-ui「skeleton」）。w／h 可給數字（px）或字串 */
export function Skeleton({ w, h, radius, sx, ...rest }) {
  return (
    <Box
      data-loading-ui=""
      aria-hidden="true"
      sx={{
        width: w,
        height: h,
        flexShrink: 0,
        borderRadius: radius,
        bgcolor: "action.hover",
        animation: "loading-ui-skeleton-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
        ...sx,
      }}
      {...rest}
    />
  );
}

Skeleton.propTypes = {
  w: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  h: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  radius: PropTypes.string,
  sx: PropTypes.object,
};
Skeleton.defaultProps = { w: "100%", h: "1em", radius: "var(--radius-xs)", sx: {} };

/** 整頁／整個區塊載入：置中的轉圈＋掃光文字。dense 用在卡片裡 */
export function PageLoading({ text, dense, sx }) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        py: dense ? 4 : 10,
        color: "primary.main",
        ...sx,
      }}
    >
      <Ring size={dense ? 1.6 : 2.2} />
      <TextShimmer>{text}</TextShimmer>
    </Box>
  );
}

PageLoading.propTypes = { text: PropTypes.string, dense: PropTypes.bool, sx: PropTypes.object };
PageLoading.defaultProps = { text: "載入中…", dense: false, sx: {} };

/** 一行字位置的載入：跳動圓點＋文字（例如下拉選單、說明句的位置） */
export function InlineLoading({ text, sx }) {
  return (
    <Box
      component="span"
      role="status"
      aria-live="polite"
      sx={{ display: "inline-flex", alignItems: "center", gap: 1, color: "text.secondary", ...sx }}
    >
      <Dots label="" />
      <Box component="span">{text}</Box>
    </Box>
  );
}

InlineLoading.propTypes = { text: PropTypes.string, sx: PropTypes.object };
InlineLoading.defaultProps = { text: "載入中…", sx: {} };

/** 清單／表格骨架：一列標題＋一行次要資訊，寬度錯落才像真的內容 */
const WIDTHS = ["62%", "48%", "71%", "55%", "66%", "44%"];

export function SkeletonRows({ rows, label, sx }) {
  return (
    <Box role="status" aria-live="polite" sx={sx}>
      <Box component="span" sx={srOnly}>
        {label}
      </Box>
      {Array.from({ length: rows }, (_, i) => (
        <Box
          key={i}
          aria-hidden="true"
          sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, py: 2, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
        >
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            <Skeleton w={WIDTHS[i % WIDTHS.length]} h="1.1em" />
            <Skeleton w={`calc(${WIDTHS[(i + 3) % WIDTHS.length]} - 20%)`} h="0.85em" />
          </Box>
          <Skeleton w="4.5em" h="1.8em" radius="var(--radius-sm)" />
        </Box>
      ))}
    </Box>
  );
}

SkeletonRows.propTypes = { rows: PropTypes.number, label: PropTypes.string, sx: PropTypes.object };
SkeletonRows.defaultProps = { rows: 4, label: "載入中", sx: {} };

/** 卡片骨架：標題＋幾行內文 */
export function SkeletonCard({ lines, label, sx }) {
  return (
    <Box role="status" aria-live="polite" sx={{ display: "flex", flexDirection: "column", gap: 1.25, py: 1, ...sx }}>
      <Box component="span" sx={srOnly}>
        {label}
      </Box>
      <Skeleton w="38%" h="1.3em" aria-hidden="true" />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} w={i === lines - 1 ? "58%" : "100%"} h="0.95em" />
      ))}
    </Box>
  );
}

SkeletonCard.propTypes = { lines: PropTypes.number, label: PropTypes.string, sx: PropTypes.object };
SkeletonCard.defaultProps = { lines: 3, label: "載入中", sx: {} };
